'use client';

import { useEffect, useRef, useState } from 'react';
import { Box, LucideIcon, MessageCircle, MonitorUp, Power } from 'lucide-react';
import { apiFetch } from '@/lib/api/apiClient';
import { useAuthStore } from '@/stores/useAuthStore';
import { BlockUsage, BoardUsage, EditorUsage } from '@/types/dashboard';
import IconAdaptive from '@/components/IconAdaptive';
import SectionTitle from '@/components/SectionTitle';
import { getBlockLabel } from '@/(routers)/boards/editor/block/definitions/categories';

const BOARD_LABELS: Record<string, string> = {
    orange: 'オレンジボード',
    blue: 'ブルーボード',
};
const labelOfBoard = (key: string) => BOARD_LABELS[key] ?? key;

const BOARD_ICONS: Record<string, string> = {
    orange: '/images/block/board_orange.svg',
    blue: '/images/block/board_blue.svg',
};
const iconOfBoard = (key: string) => BOARD_ICONS[key] ?? '/images/block/board_orange.svg';

// ブロックの先頭アイコン。
// デフォルトで no-image アイコン（Lucide）を表示し、画像 /images/block/<英名>.svg の読み込みが成功したら画像に差し替える。
function BlockIcon({ blockType, label }: { blockType: string; label: string }) {
    const imgRef = useRef<HTMLImageElement>(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        imgRef.current
            ?.decode()
            .then(() => {
                if (!cancelled) setLoaded(true);
            })
            .catch(() => {
                // 画像が存在しない（404）場合は Lucide のまま
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <>
            {!loaded && <Box className="w-5 h-5 text-color-white-base" strokeWidth={1.8} />}
            <img
                ref={imgRef}
                src={`/images/block/${blockType}.svg`}
                alt={label}
                className={loaded ? 'w-7 h-7 object-contain' : 'hidden'}
                onLoad={() => setLoaded(true)}
            />
        </>
    );
}

export default function DashboardBlockEditorDataUnit({ showTitle = true }: { showTitle?: boolean }) {
    const userScore = useAuthStore((s) => s.userScore);
    const [usage, setUsage] = useState<EditorUsage | null>(null);

    useEffect(() => {
        (async () => {
            try {
                const data = await apiFetch('/dashboard/block_editor/usage');
                setUsage({
                    boardUsage: (data.board_usage ?? []).map(
                        (b: { board_type: string; count: number }): BoardUsage => ({
                            boardType: b.board_type,
                            count: b.count,
                        }),
                    ),
                    blockUsage: (data.block_usage ?? []).map(
                        (b: { block_type: string; count: number }): BlockUsage => ({
                            blockType: b.block_type,
                            count: b.count,
                        }),
                    ),
                });
            } catch {
                setUsage({ boardUsage: [], blockUsage: [] });
            }
        })();
    }, []);

    const items: { icon: LucideIcon; label: string; value?: number }[] = [
        { icon: Power, label: 'ブロックエディター起動回数', value: userScore?.editorLaunchCount ?? 0 },
        { icon: MessageCircle, label: 'ホワイト先生質問回数', value: userScore?.whiteTeacherAnswerCount ?? 0 },
        { icon: MonitorUp, label: 'スクーミーボード書き込み回数', value: userScore?.editorWriteCount ?? 0 },
    ];

    const boardUsage = usage?.boardUsage ?? [];
    const blockUsage = (usage?.blockUsage ?? []).filter((b) => b.blockType !== 'schoomy_board');
    const maxBlockCount = blockUsage.reduce((max, { count }) => Math.max(max, count), 0);

    return (
        <div className="flex flex-col w-full section-4-b bg-color-white-base">
            {showTitle && (
                <>
                    <div className="flex w-full items-center">
                        <div className="flex flex-grow justify-start items-center w-full">
                            <SectionTitle title={`Block Editor Data.`} />
                        </div>
                    </div>
                    <div className="section-spacer-inner-y" />
                </>
            )}
            <div className="relative mt-6 pb-1">
                <div className="absolute -top-[14px] left-3 rounded-md px-3 py-0.5 flex items-center justify-center bg-color-base">
                    <span className="font-cantarell font-bold text-xs text-white">エディター利用回数</span>
                </div>
                <div className="rounded-2xl px-5 py-6" style={{ border: '1.5px solid rgba(0,0,0,0.10)' }}>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
                        {items.map(({ icon: Icon, label, value }) => (
                            <div key={label} className="flex items-center gap-3 rounded-2xl bg-color-co-gray-light p-4">
                                <div className="flex items-center justify-center w-11 h-11 rounded-full bg-color-white-base flex-none">
                                    <IconAdaptive
                                        Icon={Icon}
                                        baseSize={22}
                                        strokeWidth={1.8}
                                        className="text-color-co-gray-dark"
                                    />
                                </div>
                                <div className="flex flex-col min-w-0">
                                    <div className="font-corp font-bold text-xs text-color-base leading-tight">
                                        {label}
                                    </div>
                                    <div className="font-cantarell font-bold text-2xl text-color-c-orange leading-tight">
                                        {value}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="relative mt-8 pb-1">
                <div className="absolute -top-[14px] left-3 rounded-md px-3 py-0.5 flex items-center justify-center bg-color-base">
                    <span className="font-cantarell font-bold text-xs text-white">ボード利用回数</span>
                </div>
                <div className="rounded-2xl px-5 py-6" style={{ border: '1.5px solid rgba(0,0,0,0.10)' }}>
                    {boardUsage.length === 0 ? (
                        <div className="font-notojp font-medium text-xs text-color-co-gray-dark rounded-2xl bg-color-co-gray-light p-4">
                            まだ書き込みがありません
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
                            {boardUsage.map(({ boardType, count }) => (
                                <div
                                    key={boardType}
                                    className="flex items-center gap-3 rounded-2xl bg-color-co-gray-light p-4"
                                >
                                    <div className="flex items-center justify-center w-11 h-11 rounded-full bg-color-white-base flex-none">
                                        <img
                                            src={iconOfBoard(boardType)}
                                            alt={labelOfBoard(boardType)}
                                            className="w-10 h-10 object-contain"
                                        />
                                    </div>
                                    <div className="flex flex-col min-w-0">
                                        <div className="font-corp font-bold text-xs text-color-base leading-tight">
                                            {' '}
                                            {labelOfBoard(boardType)}
                                        </div>
                                        <div className="font-cantarell font-bold text-2xl text-color-c-orange leading-tight">
                                            {count}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="relative mt-8 pb-1">
                <div className="absolute -top-[14px] left-3 rounded-md px-3 py-0.5 flex items-center justify-center bg-color-base">
                    <span className="font-cantarell font-bold text-xs text-white">ブロック利用回数</span>
                </div>
                <div className="rounded-2xl px-5 py-6" style={{ border: '1.5px solid rgba(0,0,0,0.10)' }}>
                    {blockUsage.length === 0 ? (
                        <div className="font-notojp font-medium text-xs text-color-co-gray-dark rounded-2xl bg-color-co-gray-light p-4">
                            まだ書き込みがありません
                        </div>
                    ) : (
                        <div className="flex flex-col gap-3 rounded-2xl bg-color-co-gray-light p-4">
                            {blockUsage.map(({ blockType, count }) => (
                                <div key={blockType} className="flex items-center gap-2">
                                    <span className="flex items-center justify-center w-7 h-7 rounded-full bg-color-base-2-light flex-none">
                                        <BlockIcon blockType={blockType} label={getBlockLabel(blockType)} />
                                    </span>
                                    <div className="font-corp font-medium text-xs text-color-base w-36 flex-none whitespace-nowrap">
                                        {getBlockLabel(blockType)}
                                    </div>
                                    <div className="hidden sm:block flex-grow h-2 rounded-full bg-color-white-base overflow-hidden">
                                        <div
                                            className="h-full rounded-full bg-color-c-orange"
                                            style={{
                                                width: `${maxBlockCount > 0 ? (count / maxBlockCount) * 100 : 0}%`,
                                            }}
                                        />
                                    </div>
                                    <div className="font-cantarell font-bold text-sm text-color-c-orange w-12 text-right flex-none ml-auto sm:ml-0">
                                        {count}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
